import React from 'react';
import { MissionIdParams } from '@src/core/entities';
import { useCancelMissionMutation, useGetOrderMissionQuery } from '@src/features/order/redux';
import { useNavigate, useParams } from 'react-router-dom';
import { EnterRouteForm, SidebarLoading } from '@src/features/order/pages/upsert-mission-page/components';
import { IRouteForm } from '@src/features/order/pages/upsert-mission-page/entities';
import { useForm } from 'react-hook-form';
import { OrderMissionEntity } from '@src/core/features';
import { EMPTY_VALUE } from '@src/core/config';
import { ArcStack, ArcToolbar } from '@src/core/ui';
import { selectTerrainMode, selectWaypoints, useUpdateMissionMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { routesConfig } from '@src/core/routes';

const getDeliveryMode = (data: OrderMissionEntity) => {
  const [ order ] = data.orders;
  return order.deliveryMode;
};

const getOrderWP = (data: OrderMissionEntity) => {
  const orderWP = data.waypoints.find((w) => Boolean(w.orderId));
  return orderWP;
};

const getTakeOff = (data: OrderMissionEntity) => {
  const [wp] = data.waypoints;
  return wp;
};

const UpdateRouteSidebar: React.FC = () => {
  const navigate = useNavigate();
  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAdvanced = terrainMode === TerrainMode.ADVANCED;
  const params = useParams<MissionIdParams>();
  const { data, isLoading } = useGetOrderMissionQuery(params.missionId);
  
  const [updateMission, { isLoading: isSubmitting }] = useUpdateMissionMutation();
  const [ cancelMission, { isLoading: isCanceling } ] = useCancelMissionMutation();
  

  const goBack = () => navigate(-1);

  const onCancel = () => {
    cancelMission(params.missionId).unwrap().then(() => {
      toast.success('Mission has been canceled');
      navigate(routesConfig.orders, { replace: true });
    }).catch(() => {});
  };

  const onSubmit = (form: IRouteForm) => {
    updateMission({
      missionId: params.missionId,
      deliveryMode: form.deliveryMode,
      waypoints: wps.map((w) => ({
        orderId: w.orderId,
        altitude: w.altitude,
        latitude: w.latitude,
        longitude: w.longitude,

        speed: form.speed,
        deliveryAltitude: form.deliveryAltitude,
        ...(isAdvanced && { winchAltitude: form.winchAltitude }),
      })),
    }).unwrap().then(() => {
      toast.success('Mission has been updated');
      navigate(-1);
    }).catch(() => {
      // Error toast handled by rtkErrorLogger middleware
    });
  };

  const form = useForm<IRouteForm>({ 
    defaultValues: {
      waypoints: [],
      speed: EMPTY_VALUE,
      deliveryMode: EMPTY_VALUE,
      takeOffAltitude: EMPTY_VALUE,
      deliveryAltitude: EMPTY_VALUE,
      winchAltitude: EMPTY_VALUE,
      destinationAltitude: EMPTY_VALUE,
    } 
  });


  React.useEffect(() => {
 
    if (!data) return;

    const orderWp = getOrderWP(data);
    const takeOffWp = getTakeOff(data);

    form.setValue('deliveryMode', getDeliveryMode(data));
    form.setValue('speed', orderWp?.speed ?? EMPTY_VALUE);
    form.setValue('deliveryAltitude', orderWp?.deliveryAltitude ?? EMPTY_VALUE);
    form.setValue('winchAltitude', orderWp?.deliveryAltitude ?? EMPTY_VALUE);
    form.setValue('takeOffAltitude', String(takeOffWp?.altitude) ?? EMPTY_VALUE);
    form.setValue('destinationAltitude', String(orderWp?.altitude) ?? EMPTY_VALUE);  
  }, [data]);


  if (isLoading) {
    return <SidebarLoading />;
  }

  return (
    <ArcStack spacing={2} height='100%'>
      <ArcToolbar 
        title='3. Enter route parameters' 
        goBack={goBack} />
      <EnterRouteForm 
        form={form} 
        goBack={onCancel} 
        onSubmit={onSubmit} 
        isCanceling={isCanceling}
        isSubmitting={isSubmitting} />
    </ArcStack>
  );
};

export default UpdateRouteSidebar;